/**
 * Link normalisation for author-entered URLs (AP-5593).
 *
 * The WYSIWYG link modal stores whatever string the author types, so a URL entered without a
 * scheme — `www.connect.apiabroad.app` — reaches the renderer verbatim. The browser then resolves
 * it *relative to the current page*, which sends the user to
 * `/program-page/www.connect.apiabroad.app` instead of off-site.
 */

// Matches a URI scheme: `https:`, `mailto:`, `tel:` …
const HAS_SCHEME = /^[a-z][a-z\d+.-]*:/i;

/**
 * Returns `href` with `https://` prepended when it names a host but omits the scheme.
 *
 * Left untouched: anything already carrying a scheme, protocol-relative (`//host`), root-relative
 * (`/path`) and in-page anchors (`#id`). Root-relative links are deliberately preserved — inside
 * WYSIWYG copy those are an intentional link to another page of this app, which is exactly what
 * the accidental case only *looks* like.
 *
 * @param href - Raw href as authored.
 * @returns The normalised href, or `""` when `href` is blank.
 */
export const normalizeHref = (href: string): string => {
  const trimmed = href.trim();

  if (!trimmed) return "";
  // `startsWith("/")` covers protocol-relative `//host` as well as root-relative `/path`.
  if (HAS_SCHEME.test(trimmed) || trimmed.startsWith("/") || trimmed.startsWith("#")) {
    return trimmed;
  }

  return `https://${trimmed}`;
};

/**
 * Runs {@link normalizeHref} over every anchor in an HTML fragment.
 *
 * Parsing rather than regex-replacing keeps quoting and attribute order out of the picture. A
 * blank href is left as authored: rewriting it would not make the link work and would only
 * obscure the underlying content problem.
 *
 * @param html - HTML fragment, e.g. the output of tiptap's `generateHTML`.
 * @returns The fragment with normalised anchor hrefs.
 */
export const normalizeHtmlLinks = (html: string): string => {
  if (typeof DOMParser === "undefined") return html;

  const parsed = new DOMParser().parseFromString(html, "text/html");
  const anchors = parsed.querySelectorAll("a[href]");
  if (anchors.length === 0) return html;

  anchors.forEach((anchor) => {
    const normalized = normalizeHref(anchor.getAttribute("href") ?? "");
    if (normalized) anchor.setAttribute("href", normalized);
  });

  return parsed.body.innerHTML;
};
